import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Play, Pause, Square, Heart, Zap, MapPin, Clock, Trash2, ArrowLeft, Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const ACTIVITIES = [
  "Walk", "Jog", "Run", "Cycling", "Treadmill", "Stationary Bike",
  "Rowing", "Elliptical", "Stairmaster", "Swim", "Other",
];

const fmtDur = (s) => {
  s = Math.max(0, Math.floor(s || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
    : `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

export default function Cardio() {
  const nav = useNavigate();
  const { user } = useAuth();
  const unit = user?.distance_unit || "km";
  const [tab, setTab] = useState("log");
  const [sessions, setSessions] = useState([]);

  // Live tracker
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0); // seconds
  const startedAtRef = useRef(null);
  const tickRef = useRef(null);

  // Form (used by both manual + after live tracker)
  const [form, setForm] = useState({
    activity_type: "Run",
    distance: "",
    duration_seconds: 0,
    avg_heart_rate: "",
    max_heart_rate: "",
    calories: "",
    notes: "",
  });

  const load = async () => {
    const { data } = await api.get("/cardio");
    setSessions(data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!running) return;
    tickRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 250);
    return () => clearInterval(tickRef.current);
  }, [running]);

  const start = () => {
    startedAtRef.current = Date.now() - elapsed * 1000;
    setRunning(true);
  };
  const pause = () => setRunning(false);
  const stop = () => {
    setRunning(false);
    setForm((f) => ({ ...f, duration_seconds: elapsed }));
    setTab("log");
    toast.success(`Stopped at ${fmtDur(elapsed)} — fill in details and save`);
  };
  const reset = () => { setElapsed(0); setRunning(false); };

  const submit = async (e) => {
    e.preventDefault();
    const distance = Number(form.distance) || 0;
    const dur = Number(form.duration_seconds) || elapsed;
    if (!form.activity_type || (distance === 0 && dur === 0)) {
      toast.error("Enter at least distance or duration");
      return;
    }
    try {
      await api.post("/cardio", {
        activity_type: form.activity_type,
        distance,
        distance_unit: unit,
        duration_seconds: dur,
        avg_heart_rate: form.avg_heart_rate ? Number(form.avg_heart_rate) : null,
        max_heart_rate: form.max_heart_rate ? Number(form.max_heart_rate) : null,
        calories: form.calories ? Number(form.calories) : null,
        notes: form.notes,
      });
      toast.success("Cardio logged 🔥");
      setForm({ activity_type: "Run", distance: "", duration_seconds: 0, avg_heart_rate: "", max_heart_rate: "", calories: "", notes: "" });
      reset();
      load();
      // Auto post to feed (without blocking)
      api.post("/feed", {
        text: `Cardio · ${form.activity_type}${distance > 0 ? ` · ${distance} ${unit}` : ""}${dur > 0 ? ` · ${fmtDur(dur)}` : ""}`,
        kind: "workout",
      }).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const removeSession = async (cid) => {
    if (!window.confirm("Delete this cardio session?")) return;
    await api.delete(`/cardio/${cid}`);
    setSessions((s) => s.filter((x) => x.cardio_id !== cid));
  };

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6">
      <button onClick={() => nav(-1)} className="flex items-center text-neutral-400 hover:text-blue-500 text-xs uppercase tracking-widest mb-3">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back
      </button>
      <div className="text-xs tracking-[0.3em] uppercase text-blue-500 font-bold">CARDIO</div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase leading-none mt-1">Move Your Body</h1>
      <p className="text-sm text-neutral-400 mb-5">Walk, jog, run, ride — every step counts.</p>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-neutral-900 border border-neutral-800 rounded-none h-auto p-0 flex-wrap">
          <TabsTrigger value="log" data-testid="cardio-tab-log" className="rounded-none h-10 px-4 font-display uppercase tracking-wider data-[state=active]:bg-blue-500 data-[state=active]:text-white">Log</TabsTrigger>
          <TabsTrigger value="live" data-testid="cardio-tab-live" className="rounded-none h-10 px-4 font-display uppercase tracking-wider data-[state=active]:bg-blue-500 data-[state=active]:text-white">Live Tracker</TabsTrigger>
          <TabsTrigger value="history" data-testid="cardio-tab-history" className="rounded-none h-10 px-4 font-display uppercase tracking-wider data-[state=active]:bg-blue-500 data-[state=active]:text-white">History</TabsTrigger>
        </TabsList>

        <TabsContent value="log" className="mt-5">
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs tracking-widest uppercase text-neutral-400">Activity</Label>
              <Select value={form.activity_type} onValueChange={(v) => setForm({...form, activity_type: v})}>
                <SelectTrigger data-testid="cardio-activity-select" className="mt-2 bg-neutral-900 border-neutral-800 rounded-none h-11"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-neutral-900 border-neutral-800">
                  {ACTIVITIES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs tracking-widest uppercase text-neutral-400">Distance ({unit})</Label>
                <Input data-testid="cardio-distance-input" type="number" min={0} step={0.01} value={form.distance} onChange={(e) => setForm({...form, distance: e.target.value})} className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase text-neutral-400">Duration (mm:ss)</Label>
                <Input data-testid="cardio-duration-input" type="text" value={fmtDur(form.duration_seconds)} onChange={(e) => {
                  const parts = e.target.value.split(":").map((x) => Number(x) || 0);
                  let secs = 0;
                  if (parts.length === 3) secs = parts[0]*3600 + parts[1]*60 + parts[2];
                  else if (parts.length === 2) secs = parts[0]*60 + parts[1];
                  else secs = parts[0];
                  setForm({...form, duration_seconds: secs});
                }} placeholder="00:00" className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11 font-mono-d" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs tracking-widest uppercase text-neutral-400 flex items-center gap-1"><Heart className="w-3 h-3" /> Avg HR</Label>
                <Input data-testid="cardio-avg-hr-input" type="number" min={30} max={250} value={form.avg_heart_rate} onChange={(e) => setForm({...form, avg_heart_rate: e.target.value})} className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase text-neutral-400">Max HR</Label>
                <Input data-testid="cardio-max-hr-input" type="number" min={30} max={250} value={form.max_heart_rate} onChange={(e) => setForm({...form, max_heart_rate: e.target.value})} className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase text-neutral-400 flex items-center gap-1"><Zap className="w-3 h-3" /> Cal</Label>
                <Input data-testid="cardio-cal-input" type="number" min={0} value={form.calories} onChange={(e) => setForm({...form, calories: e.target.value})} className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
              </div>
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase text-neutral-400">Notes</Label>
              <Input data-testid="cardio-notes-input" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="Felt amazing today..." className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
            </div>
            <Button data-testid="cardio-save-btn" type="submit" className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-12 text-base">
              <Plus className="w-4 h-4 mr-2" /> Save Cardio Session
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="live" className="mt-5">
          <div className="border border-neutral-800 p-6 text-center">
            <div className="text-xs tracking-widest uppercase text-neutral-400">Stopwatch</div>
            <div data-testid="cardio-timer-display" className="font-display text-7xl sm:text-8xl text-white mt-3">{fmtDur(elapsed)}</div>
            <div className="flex justify-center gap-3 mt-6">
              {!running ? (
                <Button data-testid="cardio-start-btn" onClick={start} className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-12 px-8 text-base">
                  <Play className="w-4 h-4 mr-2" /> {elapsed > 0 ? "Resume" : "Start"}
                </Button>
              ) : (
                <Button data-testid="cardio-pause-btn" onClick={pause} className="bg-neutral-700 hover:bg-neutral-600 rounded-none uppercase tracking-wider font-display h-12 px-8 text-base">
                  <Pause className="w-4 h-4 mr-2" /> Pause
                </Button>
              )}
              <Button data-testid="cardio-stop-btn" onClick={stop} disabled={elapsed === 0} variant="outline" className="bg-transparent border-neutral-700 text-white rounded-none uppercase tracking-wider font-display h-12 px-6">
                <Square className="w-4 h-4 mr-2" /> Stop
              </Button>
            </div>
            {elapsed > 0 && !running && (
              <button onClick={reset} className="mt-4 text-xs uppercase tracking-widest text-neutral-500 hover:text-blue-500">Reset</button>
            )}
            <p className="text-xs text-neutral-500 mt-5">Stop the timer to fill in distance and details.</p>
          </div>
        </TabsContent>

        <TabsContent value="history" className="mt-5">
          {sessions.length === 0 ? (
            <div className="border border-dashed border-neutral-800 p-8 text-center text-sm text-neutral-400">
              <MapPin className="w-8 h-8 text-neutral-600 mx-auto mb-3" /> No cardio sessions yet.
            </div>
          ) : (
            <div className="space-y-2">
              {sessions.map((s) => (
                <div key={s.cardio_id} data-testid={`cardio-row-${s.cardio_id}`} className="border border-neutral-800 p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500/15 border border-blue-500/40 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg uppercase">{s.activity_type}</div>
                    <div className="text-xs text-neutral-400 flex flex-wrap gap-2">
                      {s.distance > 0 && <span><MapPin className="w-3 h-3 inline" /> {s.distance} {s.distance_unit}</span>}
                      {s.duration_seconds > 0 && <span><Clock className="w-3 h-3 inline" /> {fmtDur(s.duration_seconds)}</span>}
                      {s.avg_heart_rate && <span><Heart className="w-3 h-3 inline text-blue-500" /> {s.avg_heart_rate} bpm</span>}
                      {s.calories && <span><Zap className="w-3 h-3 inline text-blue-500" /> {s.calories} cal</span>}
                    </div>
                    <div className="text-[10px] text-neutral-500 uppercase tracking-wider mt-0.5">{new Date(s.completed_at).toLocaleString()}</div>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => removeSession(s.cardio_id)} className="text-neutral-400 hover:text-blue-500 h-8 w-8"><Trash2 className="w-3 h-3" /></Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
